import { useEffect, useMemo, useRef, useState } from "react";
import { Search, Users } from "lucide-react";

type Client = {
  id: string;
  name: string;
  avatar: string;
};

type ClientSwitcherDrawerProps = {
  isOpen: boolean;
  clients: Client[];
  selectedClientId: string;
  onSelectClient: (clientId: string) => void;
  onClose: () => void;
};

const ClientSwitcherDrawer = ({
  isOpen,
  clients,
  selectedClientId,
  onSelectClient,
  onClose,
}: ClientSwitcherDrawerProps) => {
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen) setSearchTerm("");
  }, [isOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, onClose]);

  const filteredClients = useMemo(() => {
    if (!searchTerm.trim()) return clients;
    return clients.filter((client) =>
      client.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [clients, searchTerm]);

  if (!isOpen) return null;

  return (
    <div
      ref={dropdownRef}
      className="absolute top-full right-0 mt-2 w-[300px] bg-white rounded-2xl border border-[#E5E7EB] shadow-[0_24px_80px_rgba(15,23,42,0.12)] z-[999]"
    >
      <div className="px-6 py-4 border-b border-[#E5E7EB]">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#94A3B8]" />
          <input
            type="text"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            placeholder="Search clients..."
            className="w-full rounded-full border border-[#E2E8F0] bg-[#F8FAFC] py-2 pl-10 pr-4 text-sm font-termina text-[#1F2937] placeholder:text-[#94A3B8] focus:border-[#2F8762] focus:outline-none focus:ring-2 focus:ring-[#A7F3D0]/60"
          />
        </div>
      </div>
      <div className="max-h-96 overflow-y-auto px-4 py-3">
        <ul className="flex flex-col gap-2">
          {/* Client List */}
          {filteredClients.map((client) => {
            const isSelected = client.id === selectedClientId;
            return (
              <li key={client.id}>
                <button
                  type="button"
                  onClick={() => {
                    onSelectClient(client.id);
                    onClose();
                  }}
                  className={`flex w-full items-center gap-3 rounded-xl px-4 py-3 text-left transition-all ${
                    isSelected
                      ? "bg-[#2F8762] text-white shadow-[0_8px_20px_rgba(47,135,98,0.2)]"
                      : "bg-[#F9FAFB] text-[#111827] hover:bg-[#EEF2FF]"
                  }`}
                >
                  <img
                    src={client.avatar}
                    alt={client.name}
                    className="h-10 w-10 rounded-full border border-white object-cover shadow shrink-0"
                  />
                  <div className="flex flex-col flex-1 min-w-0">
                    <span className="font-recoleta text-lg font-semibold leading-tight">{client.name}</span>
                    {isSelected && (
                      <span className="text-xs uppercase tracking-[0.15em] mt-1 opacity-90">
                        Selected
                      </span>
                    )}
                  </div>
                </button>
              </li>
            );
          })}
          {filteredClients.length === 0 && (
            <li className="rounded-xl bg-[#F8FAFC] px-4 py-8 text-center">
              <div className="flex flex-col items-center gap-3">
                <Users className="h-12 w-12 text-[#9CA3AF]" />
                <p className="text-sm font-termina text-[#6B7280]">
                  No clients found.
                </p>
              </div>
            </li>
          )}
        </ul>
      </div>
    </div>
  );
};

export default ClientSwitcherDrawer;

